import CaseCreateAttachments from "@/components/cases/case-create-attachments";
import { OnlyOfficeEditor } from "@/components/common/OnlyOfficeEditor.tsx";
import ConfirmWithPasswordModal from "@/components/confirm-with-password-modal";
import Alert from "@/components/core/alert";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import DataTable from "@/components/core/data-table";
import ComboboxCreatable from "@/components/core/forms/combox-creatable.tsx";
import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input.tsx";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { ScrollArea } from "@/components/core/scroller";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import type { FormTemplate } from "@/data/form-templates";
import { useCaseActivityMotionMutation } from "@/features/case-evaluation/api/queries/acitivities-query";
import { useEvidenceMutation } from "@/features/case-evaluation/api/queries/evidence-query.ts";
import EvidenceList from "@/features/case-evaluation/components/evidence-list";
import {
	type CaseActivity,
	type CaseActivityForm,
	type CaseActivityMotionForm,
	caseActivityMotionFormSchema,
} from "@/features/case-evaluation/types/activities";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { useDocumentConfigMutation } from "@/features/dashboard/api/queries/document-config-queries.ts";
import { queryClient } from "@/query-client";
import { getOrdinalWord } from "@/utils/format-data";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconFile, IconPlus, IconTemplate, IconTrash, IconX } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { useFieldArray } from "react-hook-form";
import { useForm } from "react-hook-form";
import CaseDefendantModal from "./case-defendant-modal";

export default function CasePartyComplaintModal({
	defendentId,
	caseId,
	stage,
	title,
	caseData,
	depth,
}: {
	defendentId: string;
	caseId: string;
	stage: string;
	title: string;
	depth: number;
	caseData: CaseRecord;
}) {
	const { closeModal } = useModalControl();
	const [isDisabled, setIsDisabeld] = useState(false);
	const caseRecordUserId = defendentId;
	const modal = useModal();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const navigate = useNavigate();
	const expungeCaseMutation = useEvidenceMutation();

	const caseActivitiesMutation = useCaseActivityMotionMutation(caseId);
	const today = new Date();
	const dateDue = new Date(today.setDate(today.getDate() + 30));
	const [annexFiles, setAnnexFiles] = useState<File[]>([]);

	const ordinalWord = getOrdinalWord(depth);

	const form = useForm<CaseActivityMotionForm>({
		resolver: zodResolver(caseActivityMotionFormSchema),
		defaultValues: {
			id: null,
			case_id: caseId,
			stage: stage,
			evidences: [],
			files: [],
			defendants: [],
			assigned_to: "Both",
			activity: `MOTION TO ADD ${ordinalWord.toUpperCase()}-PARTY COMPLAINT`,
			due_at: null,
			remarks: null,
			posting_for: null,
			move_to_next_stage: false,
			with_grant: true,
			with_compliance: false,
			action_type: "with_grant",
			select_files: "upload_files",
			defendant: {
				first_name: "",
				middle_name: null,
				last_name: "",
				region_code: null,
				province_code: null,
				city_code: null,
				barangay: null,
				address_1: null,
				address_2: null,
				zipcode: null,
			},
		},
	});

	const [files, setFiles] = useState<File[]>([]);
	const evidences = form.watch("evidences");

	const defendants = useFieldArray({
		control: form.control,
		name: "defendants",
		keyName: "id",
	});

	const [selectedDefendants, setSelectedDefendants] = useState([]);
	const defendantEmails = defendants.fields.map((defendant) => defendant.email);
	const allEmails = [defendantEmails];

	const defendantColumns: ColumnDef<Defendant>[] = [
		{
			id: "first_name",
			label: "Defendant Name",
			cellClassName: "w-[350px] cursor-pointer",
			cell: ({ data, rowIndex, onClick }) => {
				return (
					<button onClick={() => onClick?.(data, rowIndex)} type="button" className="flex flex-row items-center w-full p-2 gap-1 bg-white hover:bg-slate-100">
						{data.classification === "Individual" && (
							<span className="font-semibold text-blue-600 hover:text-blue-500">
								{`${data.first_name} ${data.middle_name ? `${data.middle_name} ` : ""}${data.last_name}`}
							</span>
						)}

						{data.classification === "Juridical" && <span className="font-semibold text-blue-600 hover:text-blue-500">{`${data.corporation}`}</span>}
					</button>
				);
			},
		},
		{
			id: "classification",
			label: "Type",
			cellClassName: "w-[350px] cursor-pointer",
			cell: ({ data }) => {
				return <span className="font-semibold">{data.classification}</span>;
			},
		},
		{
			id: "address_1",
			label: "Address",
			cell: ({ data }) =>
				data.address_1 || data.address_2 || data.barangay || data?.city?.name || data?.province?.name || data.zipcode
					? `${data.address_1 || ""} ${data.address_2 || ""} ${data.barangay || ""} ${data?.city?.name || ""} ${data?.province?.name || ""} ${data.zipcode || ""}`.trim()
					: "--",
		},
	];

	const handleSubmit = async (values: CaseActivityForm) => {
		values.files = files;
		values.attachments = files;
		values.annexes = annexFiles;
		values.posting_for = [
			{
				label: defendentId,
				value: defendentId,
			},
		];
		modal.openModal({
			allowClose: true,
			title: "Confirm Submit",
			description: "Are you sure? This action cannot be undone.",
			size: "sm",
			showHeader: true,
			showFooter: false,
			component: (
				<ConfirmWithPasswordModal
					onSubmit={async () => {
						setIsSubmitting(true);
						try {
							await caseActivitiesMutation.mutateAsync(values);
							await queryClient.invalidateQueries({
								queryKey: ["case_activities"],
							});
							await queryClient.invalidateQueries({
								queryKey: ["case_records"],
							});
							await queryClient.invalidateQueries({
								queryKey: ["case_counsels"],
							});

							await navigate({
								to: `/case-evaluation/${caseId}?tabs=case-flow`,
							});

							closeModal();
						} catch (error) {
							setIsSubmitting(false);
							console.log(error);
						}
					}}
				/>
			),
		});
	};

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full h-full gap-4">
					<div className="grid grid-cols-12 w-full gap-x-3 gap-y-2 overflow-y-auto">
						<div className="col-span-12 w-full">
							<FormField
								control={form.control}
								name="activity"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Title</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Input autoComplete="off" placeholder="Title" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
					<div className="flex flex-col w-full gap-3">
						<div className="flex flex-col w-full">
							<span className="text-lg font-semibold text-foreground-dark">Defendant/s Information</span>
							<span className="text-base text-foreground/70">
								Provide the full details of the defendant(s) in this case. Ensure that all required information is completed for accurate record-keeping.
							</span>
						</div>
						<div className="flex flex-col w-full rounded-md border border-border/50">
							{defendants.fields.length > 0 && (
								<div className="flex flex-row items-center justify-end w-full p-3 gap-4 border-b border-border/50">
									<Button
										type="button"
										size="sm"
										onClick={() => {
											const defendantModal = modal.openModal({
												title: "Add New Defendant",
												description: "Case Defendant",
												size: "lg",
												component: (
													<CaseDefendantModal
														emails={allEmails}
														caseData={caseData}
														onSubmit={(data) => {
															defendants.append(data);
															modal.closeModal(defendantModal);
														}}
													/>
												),
											});
										}}
									>
										<IconPlus size={14} stroke={2} />
										<span>Add Defendant</span>
									</Button>
								</div>
							)}

							<div className="flex flex-col w-full gap-3 border-b last:border-b-0 border-border/50">
								{defendants.fields.length === 0 ? (
									<Blanket width="sm">
										<BlanketInfo
											title="No Data"
											message="No available data for now. Please check back later or refresh the page."
											action={
												<Button
													variant="default"
													size="sm"
													type="button"
													onClick={() => {
														const defendantModal = modal.openModal({
															title: "Add New Defendant",
															description: "Case Defendant",
															size: "lg",
															component: (
																<CaseDefendantModal
																	emails={allEmails}
																	caseData={caseData}
																	onSubmit={(data) => {
																		defendants.append(data);
																		modal.closeModal(defendantModal);
																	}}
																/>
															),
														});
													}}
												>
													<IconPlus size={16} stroke={2} />
													Add Defendant
												</Button>
											}
										/>
									</Blanket>
								) : (
									<div className="col-span-12 flex flex-col w-full">
										<DataTable
											data={defendants.fields}
											columns={defendantColumns}
											onRowClick={(data: Defendant, rowIndex: number) => {
												const defendantModal = modal.openModal({
													title: "Update Defendants",
													description: "Case Defendant",
													size: "lg",
													component: (
														<CaseDefendantModal
															data={data}
															emails={allEmails}
															caseData={caseData}
															isEdit={true}
															onSubmit={(updatedData) => {
																defendants.update(rowIndex, updatedData);
																modal.closeModal(defendantModal);
															}}
														/>
													),
												});
											}}
											onRemove={(rowIndex: number) => {
												defendants.remove(rowIndex);
											}}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="flex flex-col w-full gap-3">
						<div className="flex flex-col w-full">
							<span className="text-lg font-semibold text-foreground-dark">Complaint and Annexes</span>
							<span className="text-base text-foreground/70">
								Attach Complaint, Annexes, and other Related Documents. Ensure all files are clear and legible.
							</span>
						</div>
						<div className="flex flex-col w-full">
							<CaseCreateAttachments
								title="Case Complaint"
								hasAttachments={true}
								onFilesChange={async (files) => {
									setFiles(files);
								}}
							/>
							{form.formState.errors.attachments && <p className="text-sm text-danger-600">At least 1 complaint document is required</p>}
						</div>
						<div className="flex flex-col w-full">
							<CaseCreateAttachments
								title="Annexes"
								hasAttachments={true}
								onFilesChange={async (files) => {
									setAnnexFiles(files);
								}}
							/>
						</div>
					</div>

					<div className="flex flex-col w-full gap-3">
						<div className="flex flex-col w-full">
							<span className="text-lg font-semibold text-foreground-dark">Pieces of Evidence</span>
							<span className="text-base text-foreground/70">
								Upload the Documentay Exhibits, Judicial Affidavits, and Other Pieces of Evidence. Ensure all files are clear and legible.
							</span>
						</div>
						<div className="flex flex-col w-full rounded-md border border-border/50">
							<div className="flex flex-col w-full mt-2">
								<Tabs variant="compact" defaultValue="documents" orientation="horizontal">
									<TabsList>
										<TabsTrigger value="documents">Documents</TabsTrigger>
										<TabsTrigger value="testimonial">Testimonial</TabsTrigger>
										<TabsTrigger value="object">Object</TabsTrigger>
									</TabsList>
									<TabsContent value="documents">
										<div className="flex flex-col w-full p-4">
											<EvidenceList
												data={evidences?.filter((e) => e.evidence_type === "Documentary") || []}
												schema={[
													{
														className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
														name: "exhibit",
														label: "Exhibit",
														type: "text_input",
														placeholder: "Enter exhibit",
													},
													{
														name: "description",
														label: "Description",
														type: "textarea",
														placeholder: "Enter description",
													},
													{
														name: "purpose",
														label: "Purpose",
														type: "textarea",
														placeholder: "Enter purpose",
													},
													{
														name: "file",
														label: "Filed Evidence",
														type: "dragged_file",
													},
												]}
												onAdded={async (files) => {
													form.setValue("evidences", [
														...evidences.filter((e) => e.evidence_type !== "Documentary"),
														...files.map((file) => ({
															...file,
															evidence_type: "Documentary",
															case_record_user_id: caseRecordUserId,
														})),
													]);
												}}
												onRowDelete={async (row) => {
													await expungeCaseMutation.mutateAsync({ id: row.id });
												}}
												metadata={{ evidence_type: "Documentary" }}
											/>
										</div>
									</TabsContent>
									{/* Testimonial */}
									<TabsContent value="testimonial">
										<div className="flex flex-col w-full p-4">
											<EvidenceList
												data={evidences?.filter((e) => e.evidence_type === "Testimonial") || []}
												schema={[
													// {
													// 	className: "py-2 px-3 text-left text-sm text-foreground-dark",
													// 	name: "exhibit",
													// 	label: "Exhibit",
													// 	type: "text_input",
													// 	placeholder: "Enter exhibit",
													// },
													{
														name: "witness_name",
														label: "Witness Name",
														type: "text_input",
														placeholder: "Enter witness name",
													},
													{
														name: "description",
														label: "Description",
														type: "textarea",
														placeholder: "Enter description",
													},
												]}
												onAdded={async (files) => {
													form.setValue("evidences", [
														...evidences.filter((e) => e.evidence_type !== "Testimonial"),
														...files.map((file) => ({
															...file,
															evidence_type: "Testimonial",
															case_record_user_id: caseRecordUserId,
														})),
													]);
												}}
												onRowDelete={async (row) => {
													await expungeCaseMutation.mutateAsync({ id: row.id });
												}}
												metadata={{ evidence_type: "Testimonial" }}
											/>
										</div>
									</TabsContent>
									{/* Object */}
									<TabsContent value="object">
										<div className="flex flex-col w-full">
											<div className="flex flex-col w-full p-4">
												<EvidenceList
													data={evidences?.filter((e) => e.evidence_type === "Object") || []}
													schema={[
														{
															className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
															name: "exhibit",
															label: "Exhibit",
															type: "text_input",
															placeholder: "Enter exhibit",
														},
														{
															name: "description",
															label: "Description",
															type: "textarea",
															placeholder: "Enter description",
														},
														{
															name: "purpose",
															label: "Purpose",
															type: "textarea",
															placeholder: "Enter purpose",
														},
														{
															name: "file",
															label: "Filed Evidence",
															type: "dragged_file",
														},
													]}
													onAdded={async (files) => {
														form.setValue("evidences", [
															...evidences.filter((e) => e.evidence_type !== "Object"),
															...files.map((file) => ({
																...file,
																evidence_type: "Object",
																case_record_user_id: caseRecordUserId,
															})),
														]);
													}}
													onRowDelete={async (row) => {
														await expungeCaseMutation.mutateAsync({
															id: row.id,
														});
													}}
													metadata={{ evidence_type: "Object" }}
												/>
											</div>
										</div>
									</TabsContent>
								</Tabs>
							</div>
						</div>
					</div>
				</div>
				<div className="flex flex-row justify-between w-full gap-4 mt-4">
					<Button
						variant="ghost"
						type="button"
						onClick={() => {
							closeModal();
						}}
					>
						Cancel
					</Button>
					<Button disabled={isDisabled} variant={isDisabled ? "loading" : "default"}>
						Submit
					</Button>
				</div>
			</form>
		</Form>
	);
}
